<template>
  <div class="distributor-shop">
    <section class="shop-panel">
      <header class="shop-panel-header">
        <div v-if="activeOffer && !offerDismissed" class="offer-banner" role="status">
          <i class="fas fa-percent offer-banner__icon me-2"></i>
          <span class="offer-banner__text">
            Los <strong>{{ activeOffer.role_label }}</strong> tienen
            <strong>{{ offerDiscountLabel }}</strong> de descuento en
            {{ activeOffer.applies_to_all ? 'todos los productos' : 'productos seleccionados' }}.
          </span>
          <span v-if="activeOffer.end_date" class="offer-banner__until">
            Hasta {{ formatShortDate(activeOffer.end_date) }}
          </span>
          <button type="button" class="offer-banner__close" aria-label="Cerrar promoción" @click="dismissOffer">×</button>
        </div>
        <div class="panel-toolbar">
          <p class="panel-toolbar-meta mb-0">
            {{ isWholesale ? 'Catálogo mayorista' : 'Catálogo' }} — buscá productos, ingresá cantidades y confirmá tu pedido.
          </p>
          <div class="panel-toolbar-actions">
            <div class="view-toggle btn-group" role="group" aria-label="Vista del catálogo">
              <button
                type="button"
                class="btn btn-sm"
                :class="viewMode === 'grid' ? 'btn-primary' : 'btn-outline-light'"
                title="Cuadrícula con imagen grande"
                @click="viewMode = 'grid'"
              >
                <i class="fas fa-th-large"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm"
                :class="viewMode === 'list' ? 'btn-primary' : 'btn-outline-light'"
                title="Lista compacta"
                @click="viewMode = 'list'"
              >
                <i class="fas fa-list"></i>
              </button>
            </div>
            <div
              v-if="orderLines.length > 0"
              class="cart-pill"
              role="button"
              tabindex="0"
              @click="showCart = true"
              @keydown.enter="showCart = true"
            >
              <i class="fas fa-shopping-cart me-2"></i>
              <strong>{{ totalUnits }}</strong> ud. ·
              <strong>{{ formatPrice(orderGrandTotal) }}</strong>
              <span class="cart-pill-badge">{{ orderLines.length }}</span>
            </div>
          </div>
        </div>
        <div class="d-flex gap-2 flex-wrap filters-row">
          <div class="search-wrapper flex-grow-1">
            <i class="fas fa-search search-icon"></i>
            <input
              ref="searchInput"
              v-model="search"
              type="text"
              class="form-control bg-dark border-secondary text-white ps-5"
              placeholder="Buscar producto o semillero..."
            />
          </div>
          <select v-model="filterType" class="form-select bg-dark border-secondary text-white" style="width:160px">
            <option value="">Todos los tipos</option>
            <option value="fem">Feminizada</option>
            <option value="auto">Autofloreciente</option>
            <option value="custom">Exclusivo tienda</option>
          </select>
          <select
            v-if="brands.length"
            v-model="filterBrand"
            class="form-select bg-dark border-secondary text-white"
            style="width:160px"
          >
            <option value="">Todas las marcas</option>
            <option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>
      </header>

      <div class="shop-panel-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
        </div>

        <div v-else-if="filteredProducts.length === 0" class="text-center py-5 text-muted">
          <i class="fas fa-seedling fa-3x mb-3 d-block"></i>
          <template v-if="products.length === 0">
            No hay productos publicados en la tienda.
          </template>
          <template v-else>
            Sin productos que coincidan con la búsqueda.
          </template>
        </div>

        <div v-else-if="viewMode === 'grid'" class="catalog-grid">
          <DistributorProductCard
            v-for="product in filteredProducts"
            :key="product.id"
            :product="product"
            :quantities="quantities"
            :subtotal="productSubtotal(product)"
            @set-qty="setQty"
            @open-info="openProductDetail"
          />
        </div>

        <div
          v-else
          class="product-table"
          :style="{ '--blister-cols': blisterColumns.length }"
        >
        <!-- Table header -->
        <div class="table-header">
          <div class="col-product">Producto</div>
          <div class="col-type">Tipo</div>
          <div class="col-blister" v-for="bc in blisterColumns" :key="bc">{{ bc }}</div>
          <div class="col-subtotal">Subtotal</div>
          <div class="col-info" aria-hidden="true"></div>
        </div>

        <!-- Product rows -->
        <div
          v-for="product in filteredProducts"
          :key="product.id"
          class="table-row"
          :class="{ 'has-items': productSubtotal(product) > 0 }"
        >
          <!-- Product info -->
          <div class="col-product">
            <div class="d-flex align-items-center gap-2">
              <ProductImage
                :product="product"
                :alt="product.name"
                class="product-thumb product-thumb--clickable"
                role="button"
                tabindex="0"
                @click="openProductDetail(product)"
                @keydown.enter="openProductDetail(product)"
              />
              <div class="flex-grow-1 min-w-0 col-product-text">
                <span class="fw-medium product-name-link" @click="openProductDetail(product)">
                  {{ product.name }}
                </span>
                <small v-if="product.brand_name || product.brand" class="text-muted d-block">
                  {{ product.brand_name || product.brand }}
                </small>
              </div>
            </div>
          </div>

          <!-- Type -->
          <div class="col-type">
            <span class="type-badge fem" v-if="product.type === 'fem'">Fotoperiódica</span>
            <span class="type-badge auto" v-else-if="product.type === 'auto'">Autofloreciente</span>
          </div>

          <!-- Quantity inputs per blister -->
          <div class="col-blister" v-for="bc in blisterColumns" :key="bc">
            <template v-if="getBlister(product, bc)">
              <div class="blister-cell">
                <div class="blister-price">
                  <span v-if="getBlister(product, bc).prices_visible !== false">
                    <span
                      v-if="getBlister(product, bc).offer_discount_percent > 0 && getBlister(product, bc).list_price"
                      class="blister-price__list"
                    >{{ formatPrice(getBlister(product, bc).list_price) }}</span>
                    <span
                      :class="{ 'blister-price__final--offer': getBlister(product, bc).offer_discount_percent > 0 }"
                    >{{ formatPrice(getBlister(product, bc).final_price) }}</span>
                  </span>
                  <span v-else class="text-muted small">—</span>
                </div>
                <div class="qty-wrapper">
                  <button
                    class="qty-btn"
                    @click="decrement(product, getBlister(product, bc))"
                    :disabled="getQty(product, getBlister(product, bc)) <= 0"
                  >−</button>
                  <input
                    type="number"
                    class="qty-input"
                    :value="getQty(product, getBlister(product, bc))"
                    min="0"
                    :max="getBlister(product, bc).stock"
                    @change="e => setQty(product, getBlister(product, bc), parseInt(e.target.value) || 0)"
                    :disabled="getBlister(product, bc).stock === 0 || getBlister(product, bc).prices_visible === false"
                  />
                  <button
                    class="qty-btn"
                    @click="increment(product, getBlister(product, bc))"
                    :disabled="getBlister(product, bc).stock === 0 || getBlister(product, bc).prices_visible === false"
                  >+</button>
                </div>
                <transition name="stock-warn">
                  <div
                    v-if="stockWarnings[lineKey(product, getBlister(product, bc))]"
                    class="stock-warning"
                  >Sin stock disponible</div>
                </transition>
                <div class="stock-label" :class="{ 'text-danger': getBlister(product, bc).stock === 0 }">
                  {{ getBlister(product, bc).stock === 0 ? 'Sin stock' : 'En stock' }}
                </div>
              </div>
            </template>
            <div v-else class="blister-cell empty">—</div>
          </div>

          <!-- Row subtotal -->
          <div class="col-subtotal">
            <span v-if="productSubtotal(product) > 0" class="fw-bold row-subtotal">
              {{ formatPrice(productSubtotal(product)) }}
            </span>
            <span v-else class="text-muted">—</span>
          </div>

          <div class="col-info">
            <button
              type="button"
              class="btn btn-outline-light list-info-btn"
              @click="openProductDetail(product)"
            >
              <i class="fas fa-info-circle me-2"></i>Más info
            </button>
          </div>
        </div>
        </div>
      </div>

      <footer v-if="orderLines.length > 0" class="order-summary-bar px-3">
        <div class="order-summary-detail">
          <span class="text-muted small">{{ orderLines.length }} producto(s) · {{ totalUnits }} unidades</span>
          <span class="order-summary-total">Total estimado: {{ formatPrice(orderGrandTotal) }}</span>
        </div>
        <div class="order-summary-actions">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearOrder">
            <i class="fas fa-trash me-1"></i>Limpiar
          </button>
          <button type="button" class="btn btn-primary btn-sm" @click="showCart = true">
            <i class="fas fa-eye me-1"></i>Revisar pedido
          </button>
        </div>
      </footer>
    </section>

    <DistributorProductDetailModal
      :product="detailProduct"
      :quantities="quantities"
      :line-subtotal="detailProduct ? productSubtotal(detailProduct) : 0"
      @close="detailProduct = null"
      @set-qty="setQty"
    />

    <DistributorOrderDrawer
      v-model="showCart"
      v-model:checkout-form="checkoutForm"
      v-model:payment-method="paymentMethod"
      v-model:shipping-method="shippingMethod"
      v-model:coupon-code="couponCode"
      :lines="orderLines"
      :subtotal="orderTotal"
      :total-units="totalUnits"
      :shipping-enabled="shippingEnabled"
      :origin-cp="originPostalCode"
      :free-shipping-info="freeShippingInfo"
      :mercado-pago-enabled="mercadoPagoEnabled"
      :submitting="submitting"
      :submit-error="submitError"
      :min-purchase="minPurchase"
      :applied-coupon="appliedCoupon"
      :coupon-discount="couponDiscount"
      :offer-discount="offerDiscount"
      :coupon-loading="couponLoading"
      :coupon-error="couponError"
      @remove-line="removeLine"
      @update-qty="onCartQtyUpdate"
      @quoted="onQuoted"
      @submit="submitOrder"
      @apply-coupon="applyCoupon"
      @remove-coupon="removeCoupon"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive, watch } from 'vue'
import Cookies from 'js-cookie'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import apiClient from '@/services/apiClient'
import { getIngresoCatalog } from '@/services/ingreso'
import { mapIngresoCatalogToDistributorProducts, presentationLabel } from '@/utils/ingresoCatalog'
import { distributorLineKey } from '@/utils/distributorProduct'
import { getUserInfo } from '@/services/auth'
import { canAccessWholesale } from '@/utils/b2bAccess'
import { getShippingPublicConfig } from '@/services/shipping'
import { getMercadoPagoConfig, createPayment } from '@/services/payment'
import { checkFreeShipping, validateCoupon, normalizeCouponForCart, getMyActiveOffer } from '@/services/marketing'
import DistributorOrderDrawer from '@/components/distributor/DistributorOrderDrawer.vue'
import DistributorProductCard from '@/components/distributor/DistributorProductCard.vue'
import ProductImage from '@/components/ProductImage.vue'
import DistributorProductDetailModal from '@/components/distributor/DistributorProductDetailModal.vue'

const router = useRouter()
const toast = useToast()

const isWholesale = ref(canAccessWholesale())

// State
const products = ref([])
const brands = ref([])
const loading = ref(true)
const search = ref('')
const filterType = ref('')
const filterBrand = ref('')
const viewMode = ref('grid')
const showCart = ref(false)
const detailProduct = ref(null)
const submitting = ref(false)
const submitError = ref('')
const minPurchase = ref(0)
const shippingEnabled = ref(false)
const originPostalCode = ref('')
const freeShippingInfo = ref(null)
const shippingQuoteId = ref(null)
const selectedRate = ref(null)
const mercadoPagoEnabled = ref(false)
const paymentMethod = ref('cash')
const shippingMethod = ref('correo_argentino')

const couponCode = ref('')
const couponLoading = ref(false)
const couponError = ref('')
const appliedCoupon = ref(null)
const activeOffer = ref(null)
const offerDismissed = ref(false)
// Una versión distinta vuelve a mostrarse; la cookie solo controla presentación.
const offerCookieKey = () => `shop_offer_${activeOffer.value?.id}`
const offerCookieVersion = () => JSON.stringify([
  activeOffer.value?.name, activeOffer.value?.discount, activeOffer.value?.end_date,
  activeOffer.value?.role_label, activeOffer.value?.revision,
])
const dismissOffer = () => {
  offerDismissed.value = true
  const end = new Date(activeOffer.value.end_date)
  Cookies.set(offerCookieKey(), offerCookieVersion(), {
    expires: Number.isNaN(end.getTime()) ? 30 : end,
    sameSite: 'Lax', secure: window.location.protocol === 'https:', path: '/',
  })
}

// { key: "productId-blisterId" → quantity }
const quantities = reactive({})

const checkoutForm = reactive({
  recipient_name: '',
  customer_phone: '',
  customer_email: '',
  shipping_street: '',
  shipping_number: '',
  shipping_neighborhood: '',
  shipping_city: '',
  shipping_postal_code: '',
  shipping_address: '',
  shipping_agency_id: '',
  shipping_agency_name: '',
  notes: '',
})

// Helpers
const formatPrice = (v) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(v || 0)

const formatShortDate = (d) =>
  d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }) : ''

const offerDiscountLabel = computed(() => {
  const pct = Number(activeOffer.value?.discount) || 0
  const rounded = Math.round(pct * 100) / 100
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`
})

const loadActiveOffer = async () => {
  try {
    const data = await getMyActiveOffer()
    activeOffer.value = data?.active ? data : null
    offerDismissed.value = !!activeOffer.value && Cookies.get(offerCookieKey()) === offerCookieVersion()
  } catch {
    activeOffer.value = null
  }
}

const lineKey = distributorLineKey

const blisterPresentation = (product, blister) => {
  const item = {
    source: blister.source,
    seed_count: blister.seed_count,
    custom_seed_count: blister.custom_seed_count,
    product_type: product.type,
  }
  const label = presentationLabel(item)
  if (label && label !== 'Unitario') return label
  return `${blister.seed_count} ${['auto', 'fem'].includes(product.type) ? 'semillas' : 'unidades'}`
}

const getBlister = (product, seedCount) =>
  product.blisters?.find(b => String(b.seed_count) === String(seedCount)) || null

const getQty = (product, blister) => quantities[lineKey(product, blister)] || 0

const setQty = (product, blister, qty) => {
  const k = lineKey(product, blister)
  if (qty <= 0) {
    delete quantities[k]
  } else {
    quantities[k] = Math.min(qty, blister.stock)
  }
}

// Mensaje inline "sin stock" al intentar agregar más de lo disponible
const stockWarnings = reactive({})
const stockWarningTimers = {}

const showStockWarning = (product, blister) => {
  const k = lineKey(product, blister)
  stockWarnings[k] = Date.now()
  clearTimeout(stockWarningTimers[k])
  stockWarningTimers[k] = setTimeout(() => {
    delete stockWarnings[k]
    delete stockWarningTimers[k]
  }, 2000)
}

const increment = (product, blister) => {
  if (getQty(product, blister) >= blister.stock) {
    showStockWarning(product, blister)
    return
  }
  setQty(product, blister, getQty(product, blister) + 1)
}
const decrement = (product, blister) => setQty(product, blister, getQty(product, blister) - 1)

const openProductDetail = (product) => {
  detailProduct.value = product
}

// Unique seed_count columns across all visible products
const blisterColumns = computed(() => {
  const counts = new Set()
  filteredProducts.value.forEach(p => p.blisters?.forEach(b => counts.add(String(b.seed_count))))
  return [...counts].sort((a, b) => Number(a) - Number(b))
})

const filteredProducts = computed(() => {
  let result = products.value
  if (search.value) {
    const q = search.value.toLowerCase()
    result = result.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.brand_name || '').toLowerCase().includes(q)
    )
  }
  if (filterType.value) result = result.filter(p => p.type === filterType.value)
  if (filterBrand.value) result = result.filter(p => p.brand === filterBrand.value || p.brand_id === filterBrand.value)
  return result
})

const productSubtotal = (product) =>
  (product.blisters || []).reduce((sum, b) => {
    const qty = getQty(product, b)
    return sum + qty * (b.final_price || 0)
  }, 0)

const orderLines = computed(() => {
  const lines = []
  for (const [key, qty] of Object.entries(quantities)) {
    if (!qty) continue
    const product = products.value.find((p) =>
      p.blisters?.some((b) => lineKey(p, b) === key)
    )
    const blister = product?.blisters?.find((b) => lineKey(product, b) === key)
    if (product && blister) {
      const presentation = blisterPresentation(product, blister)
      lines.push({
        key,
        catalogItemId: blister.catalogItemId,
        productId: product.id,
        blisterId: blister.id,
        productName: product.name,
        displayName: `${product.name} — ${presentation}`,
        presentation,
        seedCount: blister.seed_count,
        price: blister.final_price || 0,
        listPrice: blister.list_price ?? null,
        offerPercent: blister.offer_discount_percent || 0,
        quantity: qty,
        stock: blister.stock,
      })
    }
  }
  return lines
})

const totalUnits = computed(() => orderLines.value.reduce((s, l) => s + l.quantity, 0))
const orderTotal = computed(() => orderLines.value.reduce((s, l) => s + l.price * l.quantity, 0))

// Ahorro por ofertas especiales (ya incluido en el precio unitario de cada línea).
const offerDiscount = computed(() =>
  orderLines.value.reduce((sum, l) => {
    if (!l.offerPercent || !l.listPrice) return sum
    const saved = Number(l.listPrice) - Number(l.price)
    return saved > 0 ? sum + saved * l.quantity : sum
  }, 0)
)

const couponDiscount = computed(() => {
  if (!appliedCoupon.value || appliedCoupon.value.free_shipping) return 0
  const sub = orderTotal.value
  if (appliedCoupon.value.discount_percentage > 0) {
    const discount = (sub * appliedCoupon.value.discount_percentage) / 100
    return Math.round(Math.min(discount, appliedCoupon.value.max_amount || sub, sub) * 100) / 100
  }
  return Math.min(appliedCoupon.value.discount_amount || 0, sub)
})

const orderTotalAfterCoupon = computed(() => Math.max(0, orderTotal.value - couponDiscount.value))

const applyCoupon = async () => {
  if (!couponCode.value.trim()) return
  couponLoading.value = true
  couponError.value = ''
  try {
    const res = await validateCoupon(couponCode.value, orderTotal.value)
    if (!res.valid) {
      couponError.value = res.reason || 'Cupón inválido o vencido'
      return
    }
    appliedCoupon.value = normalizeCouponForCart(res)
    toast.success('Cupón aplicado')
    await refreshFreeShipping()
  } catch (e) {
    couponError.value = e.response?.data?.reason || e.response?.data?.error || e.response?.data?.detail || 'Cupón inválido o vencido'
  } finally {
    couponLoading.value = false
  }
}

const removeCoupon = () => {
  appliedCoupon.value = null
  couponCode.value = ''
  couponError.value = ''
  refreshFreeShipping()
}

const clearOrder = () => {
  for (const k of Object.keys(quantities)) delete quantities[k]
  shippingQuoteId.value = null
  selectedRate.value = null
  removeCoupon()
}

const refreshFreeShipping = async () => {
  try {
    freeShippingInfo.value = await checkFreeShipping(
      orderTotal.value,
      appliedCoupon.value?.id || null,
    )
  } catch {
    freeShippingInfo.value = null
  }
}

watch([orderTotal, appliedCoupon], refreshFreeShipping)

watch(viewMode, (mode) => {
  localStorage.setItem('distributorCatalogView', mode)
})

const onQuoted = ({ quoteId, selectedRate: rate }) => {
  shippingQuoteId.value = quoteId
  selectedRate.value = rate
}

const onCartQtyUpdate = ({ key, quantity }) => {
  if (quantity <= 0) {
    delete quantities[key]
  } else {
    const product = products.value.find((p) =>
      p.blisters?.some((b) => lineKey(p, b) === key)
    )
    const blister = product?.blisters?.find((b) => lineKey(product, b) === key)
    if (blister) quantities[key] = Math.min(quantity, blister.stock)
  }
  shippingQuoteId.value = null
  selectedRate.value = null
}

const canSubmit = computed(() => {
  if (!orderLines.value.length) return false
  if (usesCorreoArgentino.value && !shippingQuoteId.value) return false
  if (usesCorreoArgentino.value && selectedRate.value) {
    const t = selectedRate.value.deliveredType || selectedRate.value.deliveryType || ''
    if (t === 'S' && !checkoutForm.shipping_agency_id) return false
  }
  return true
})

const grandTotal = computed(() => {
  let total = orderTotalAfterCoupon.value
  if (usesCorreoArgentino.value && selectedRate.value) {
    const ship = freeShippingInfo.value?.free_shipping ? 0 : Number(selectedRate.value.price || 0)
    total += ship
  }
  return total
})

const orderGrandTotal = computed(() => grandTotal.value)

const usesCorreoArgentino = computed(
  () => shippingEnabled.value && shippingMethod.value === 'correo_argentino'
)

const removeLine = (line) => {
  delete quantities[line.key]
}

const resetCheckoutForm = () => {
  const email = checkoutForm.customer_email
  Object.assign(checkoutForm, {
    recipient_name: '',
    customer_phone: '',
    customer_email: email,
    shipping_street: '',
    shipping_number: '',
    shipping_neighborhood: '',
    shipping_city: '',
    shipping_postal_code: '',
    shipping_address: '',
    shipping_agency_id: '',
    shipping_agency_name: '',
    notes: '',
  })
}

const submitOrder = async () => {
  if (!canSubmit.value) return
  if ((minPurchase.value || 0) > 0 && orderTotal.value < minPurchase.value) {
    submitError.value = `El monto minimo de compra es ${formatPrice(minPurchase.value)}. Te faltan ${formatPrice(minPurchase.value - orderTotal.value)} para llegar al minimo.`
    toast.error('No alcanzas el monto minimo de compra para confirmar el pedido')
    return
  }
  submitting.value = true
  submitError.value = ''
  try {
    const deliveryType = usesCorreoArgentino.value && shippingQuoteId.value ? 'delivery' : 'pickup'
    const payload = {
      recipient_name: checkoutForm.recipient_name.trim(),
      customer_phone: checkoutForm.customer_phone.trim(),
      customer_email: checkoutForm.customer_email.trim(),
      shipping_address: checkoutForm.shipping_address || [
        checkoutForm.shipping_street,
        checkoutForm.shipping_number,
        checkoutForm.shipping_city,
        checkoutForm.shipping_postal_code,
      ].filter(Boolean).join(', '),
      shipping_street: checkoutForm.shipping_street,
      shipping_number: checkoutForm.shipping_number,
      shipping_neighborhood: checkoutForm.shipping_neighborhood,
      shipping_city: checkoutForm.shipping_city,
      shipping_postal_code: checkoutForm.shipping_postal_code,
      delivery_type: deliveryType,
      payment_method: paymentMethod.value === 'mercadopago' ? 'online' : 'cash',
      notes: checkoutForm.notes,
      products: orderLines.value.map((l) => ({
        catalog_item_id: l.catalogItemId,
        unit_price: l.price,
        quantity: l.quantity,
        variant_display: l.presentation,
      })),
    }
    if (appliedCoupon.value?.code) {
      payload.coupon_code = appliedCoupon.value.code
    }
    if (usesCorreoArgentino.value && shippingQuoteId.value) {
      payload.shipping_quote_id = shippingQuoteId.value
      if (checkoutForm.shipping_agency_id) {
        payload.shipping_agency_id = checkoutForm.shipping_agency_id
        const agency = checkoutForm.shipping_agency_name
        if (agency) payload.shipping_agency_name = agency
      }
    }
    const res = await apiClient.post('/orders/', payload)
    const orderId = res.data?.id

    if (paymentMethod.value === 'mercadopago' && orderId) {
      try {
        const payment = await createPayment(orderId)
        if (payment?.init_point) {
          clearOrder()
          showCart.value = false
          toast.success('Redirigiendo a Mercado Pago...')
          setTimeout(() => {
            window.location.href = payment.init_point
          }, 800)
          return
        }
        throw new Error('No se recibió el enlace de pago')
      } catch (payErr) {
        const msg =
          payErr.response?.data?.error ||
          payErr.message ||
          'Error al iniciar Mercado Pago'
        submitError.value = msg
        toast.error('Tu pedido fue creado. Podés pagarlo desde Mis pedidos.')
        router.push(`/pedido/${orderId}`)
        return
      }
    }

    clearOrder()
    showCart.value = false
    resetCheckoutForm()
    toast.success('¡Pedido enviado! Recibirás confirmación por email.')
    router.push(orderId ? `/pedido/${orderId}` : '/panel/mis-pedidos')
  } catch (e) {
    const data = e.response?.data
    submitError.value =
      data?.min_purchase_error ||
      data?.coupon?.[0] ||
      data?.coupon ||
      data?.shipping_quote_id?.[0] ||
      data?.detail ||
      data?.error ||
      (typeof data === 'object' ? Object.values(data).flat().join(' ') : null) ||
      'Error al enviar el pedido'
  } finally {
    submitting.value = false
  }
}

// Load data
onMounted(async () => {
  document.title = 'Catálogo - Kame Seeds'
  const savedView = localStorage.getItem('distributorCatalogView')
  if (savedView === 'grid' || savedView === 'list') viewMode.value = savedView
  try {
    const [catalogRes, shipCfg, mpCfg, userInfo] = await Promise.all([
      getIngresoCatalog({ page_size: 500 }),
      getShippingPublicConfig().catch(() => ({ is_enabled: false })),
      getMercadoPagoConfig().catch(() => ({ enabled: false })),
      getUserInfo().catch(() => null),
      loadActiveOffer(),
    ])
    const catalogItems = Array.isArray(catalogRes) ? catalogRes : (catalogRes.results || [])
    products.value = mapIngresoCatalogToDistributorProducts(catalogItems)
    brands.value = []
    shippingEnabled.value = !!shipCfg.is_enabled
    originPostalCode.value = shipCfg.origin_postal_code || ''
    mercadoPagoEnabled.value = !!mpCfg.enabled
    if (mercadoPagoEnabled.value) paymentMethod.value = 'mercadopago'
    if (userInfo) {
      minPurchase.value = parseFloat(userInfo?.effective_min_purchase ?? 0) || 0
      checkoutForm.customer_email = userInfo.email || ''
      checkoutForm.recipient_name = [userInfo.first_name, userInfo.last_name].filter(Boolean).join(' ') || userInfo.username || ''
      checkoutForm.customer_phone = userInfo.phone_number || ''
      if (userInfo.address) checkoutForm.shipping_street = userInfo.address
      if (userInfo.locality) checkoutForm.shipping_city = userInfo.locality
      if (userInfo.postal_code) checkoutForm.shipping_postal_code = userInfo.postal_code
    }
    await refreshFreeShipping()
  } catch {
    toast.error('Error al cargar el catálogo')
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.distributor-shop {
  color: var(--white-color);
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 11rem);
}

.shop-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.shop-panel-header {
  flex-shrink: 0;
}

.offer-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem 0.6rem;
  padding: 0.6rem 1rem;
  margin-bottom: 0.85rem;
  border-radius: 10px;
  background: linear-gradient(90deg, rgba(255, 236, 0, 0.18), rgba(255, 236, 0, 0.05));
  border: 1px solid rgba(255, 236, 0, 0.55);
  color: #ffe873;
  font-size: 0.9rem;
  line-height: 1.35;
}

.offer-banner__icon {
  color: var(--primary-color, #ffec00);
  font-size: 1rem;
}

.offer-banner__close {
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 1.5rem;
  min-width: 44px;
  min-height: 44px;
  cursor: pointer;
}

.offer-banner__text strong {
  color: #fff;
  font-weight: 700;
}

.offer-banner__until {
  margin-left: auto;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 236, 0, 0.8);
  white-space: nowrap;
}

.shop-panel-header .panel-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.panel-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
}

.view-toggle .btn {
  min-width: 40px;
}

.catalog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
}

.filters-row {
  margin-bottom: 0.25rem;
}

.shop-panel-body {
  flex: 1;
  overflow-y: auto;
  min-height: 160px;
  padding-right: 0.25rem;
}

.order-summary-bar {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.85rem 0;
  border-top: 1px solid var(--panel-border);
  background: rgba(0, 0, 0, 0.15);
}

.order-summary-detail {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.order-summary-total {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--white-color);
}

.order-summary-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.search-wrapper { position: relative; }
.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--panel-text-muted);
  pointer-events: none;
  z-index: 1;
}

/* Cart pill */
.cart-pill {
  display: flex;
  align-items: center;
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 20px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--white-color);
  position: relative;
  transition: background 0.15s;
}
.cart-pill:hover { background: rgba(0, 0, 0, 0.3); }
.cart-pill-badge {
  background: #e5484d;
  color: #fff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  margin-left: 8px;
}

/* Product table — ancho completo; Más info al extremo derecho */
.product-table {
  width: 100%;
  overflow-x: auto;
}

.table-header,
.table-row {
  display: grid;
  width: 100%;
  min-width: 100%;
  align-items: center;
  column-gap: 0.5rem;
  grid-template-columns:
    minmax(200px, 1fr)
    72px
    repeat(var(--blister-cols, 1), minmax(108px, 120px))
    88px
    minmax(108px, auto);
  border-bottom: 1px solid var(--panel-border-muted);
}

.table-header {
  background: rgba(0, 0, 0, 0.12);
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--panel-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.5rem 0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.table-row {
  background: transparent;
  padding: 0.75rem 0;
  transition: background 0.1s;
  min-height: 80px;
}
.table-row:hover { background: var(--panel-surface-hover); }
.table-row.has-items { background: var(--panel-surface-active); }

.col-product { padding: 0 0.75rem; min-width: 0; }
.col-type { padding: 0 0.35rem; justify-self: start; }
.col-blister { padding: 0 0.35rem; text-align: center; justify-self: center; }
.col-subtotal { padding: 0 0.35rem; text-align: right; justify-self: end; }
.col-info {
  padding: 0 0.75rem 0 0;
  justify-self: end;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.col-product-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.15rem;
}

.list-info-btn {
  min-height: 2.35rem;
  padding: 0.4rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  border-radius: 8px;
  white-space: nowrap;
}

.row-subtotal {
  color: var(--primary-color, #ffec00);
}

.product-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--panel-border);
}
.product-thumb-placeholder {
  width: 40px;
  height: 40px;
  background: var(--panel-surface);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--panel-text-muted);
  font-size: 1rem;
}

.product-thumb--clickable {
  cursor: pointer;
  transition: opacity 0.15s, box-shadow 0.15s;
}

.product-thumb--clickable:hover,
.product-thumb--clickable:focus-visible {
  opacity: 0.9;
  box-shadow: 0 0 0 2px rgba(255, 236, 0, 0.35);
}

.product-name-link {
  cursor: pointer;
}

.product-name-link:hover {
  color: var(--primary-color, #ffec00);
}

.type-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}
.type-badge.regular    { background: rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.65); }
.type-badge.feminized  { background: rgba(236, 106, 31, 0.15); color: #f0a070; }
.type-badge.autoflower { background: rgba(255, 236, 0, 0.12); color: var(--primary-color); }

.blister-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.blister-cell.empty { color: var(--panel-text-muted); font-size: 1.2rem; padding: 0.5rem 0; }
.blister-price { font-size: 0.72rem; color: rgba(255, 255, 255, 0.7); }
.blister-price__list { display: block; color: rgba(255, 255, 255, 0.4); text-decoration: line-through; font-size: 0.66rem; }
.blister-price__final--offer { color: var(--primary-color, #ffec00); font-weight: 700; }
.stock-label   { font-size: 0.65rem; color: var(--panel-text-muted); }

.stock-warning {
  font-size: 0.65rem;
  font-weight: 600;
  color: #ff5a5a;
  line-height: 1.2;
  text-align: center;
  max-width: 90px;
}

.stock-warn-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.stock-warn-leave-active { transition: opacity 0.4s ease, transform 0.4s ease; }
.stock-warn-enter-from { opacity: 0; transform: translateY(4px); }
.stock-warn-leave-to   { opacity: 0; transform: translateY(-6px); }

/* Qty controls */
.qty-wrapper {
  display: flex;
  align-items: center;
  gap: 2px;
}
.qty-btn {
  width: 24px;
  height: 24px;
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  transition: background 0.1s;
}
.qty-btn:hover:not(:disabled) { background: rgba(0, 0, 0, 0.3); }
.qty-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.qty-input {
  width: 40px;
  text-align: center;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid var(--panel-border);
  color: var(--white-color);
  border-radius: 4px;
  font-size: 0.82rem;
  padding: 2px 4px;
}
.qty-input::-webkit-inner-spin-button { display: none; }
.qty-input:disabled { opacity: 0.3; }

@media (max-width: 576px) {
  .order-summary-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .order-summary-actions .btn {
    flex: 1;
  }
}

</style>
